import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Notifications = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "customer-notifications",
        "order-updates",
        "order-exceptions",
        "shipping-status",
        "returns",
        "customer-account",
        "staff-notifications",
        "new-order-alerts",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Notifications | xPage Help Center"
        description="Learn how to manage customer and staff notifications in xPage. Customize order updates, shipping alerts, returns, account messages, and internal staff alerts to improve communication and operational efficiency."
        keywords="xPage notifications, customer notifications, staff notifications, order updates, shipping alerts, return notifications, new order alerts, email templates, automated messages"
        canonical="https://help.xpage.ai/help/settings/Notifications"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Notifications
          </h1>
          <p className="text-sm text-systemLabel">
            The Notifications section in Xpage allows you to manage all
            automated communications between your store, customers, and staff.
            By setting up notifications strategically, you ensure timely
            updates, improve customer satisfaction, and streamline internal
            operations. Each notification can be customized with prebuilt
            templates or enhanced with AI for a professional, on-brand message.
          </p>
        </header>

        {/* Customer Notifications Section */}
        <section id="customer-notifications" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Customer Notifications
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              <p className="text-sm text-systemLabel">
                These notifications keep your customers informed throughout
                their shopping journey:
              </p>

              {/* Order Updates */}
              <div id="order-updates" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Order Updates
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Processing
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Notify customers when their order is being prepared.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Confirmation
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Confirm that an order has been successfully placed.
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Shipping Confirmation
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Alert customers when their order is shipped.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Shipping Updates
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Provide real-time updates on the shipping status.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Order Exceptions */}
              <div id="order-exceptions" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Order Exceptions
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Edited
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Inform customers when changes are made to their order.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Canceled
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Notify customers of cancellations.
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Refund
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Update customers when refunds are processed.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Shipping Status */}
              <div id="shipping-status" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Shipping Status
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Out for Delivery
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Let customers know their order is on the way.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Delivered
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Confirm that the order has been successfully delivered.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Returns */}
              <div id="returns" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Returns
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Return Created
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Notify customers when a return request has been
                        submitted.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Customer Account */}
              <div id="customer-account" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Customer Account
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Customer Account Invite
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Send an invitation to create a store account.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Customer Account Welcome
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Welcome new users and provide login details.
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Password Reset
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Enable customers to securely reset their passwords.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
                <p className="text-sm text-systemLabel">
                  Customization: Each customer notification comes with an
                  editable email template. Use AI-assisted tools to craft
                  professional, on-brand messages that improve engagement and
                  clarity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Staff Notifications Section */}
        <section id="staff-notifications" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Staff Notifications
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              <p className="text-sm text-systemLabel">
                Keep your team informed about store activity and order
                management:
              </p>

              {/* New Order Alerts */}
              <div id="new-order-alerts" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  New Order Alerts
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          New Order
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Notify staff when a new order is placed.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Paid
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Confirm that a customer has successfully completed
                        payment.
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Order Fulfilled
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Inform staff when an order has been fulfilled and
                        shipped.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
                <p className="text-sm text-systemLabel">
                  Preview: All staff notifications can be previewed to ensure
                  clarity and accuracy before activation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion Section */}
        <section className="scroll-mt-20">
          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4">
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                By configuring Notifications in Xpage, you maintain seamless
                communication with both customers and staff, reduce inquiries,
                improve operational efficiency, and create a professional,
                trustworthy shopping experience.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#customer-notifications"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "customer-notifications"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Customer Notifications
              </a>
            </li>
            <li>
              <a
                href="#order-updates"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "order-updates"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Order Updates
              </a>
            </li>
            <li>
              <a
                href="#order-exceptions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "order-exceptions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Order Exceptions
              </a>
            </li>
            <li>
              <a
                href="#shipping-status"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "shipping-status"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Shipping Status
              </a>
            </li>
            <li>
              <a
                href="#returns"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "returns"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Returns
              </a>
            </li>
            <li>
              <a
                href="#customer-account"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "customer-account"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Customer Account
              </a>
            </li>
            <li>
              <a
                href="#staff-notifications"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "staff-notifications"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Staff Notifications
              </a>
            </li>
            <li>
              <a
                href="#new-order-alerts"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "new-order-alerts"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                New Order Alerts
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
